import React, { useState } from 'react';
import { View, Text, Pressable, StyleSheet, Platform } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { ACCESS_YES, ACCESS_NO, ACCESS_UNKNOWN, ACCESS_SOURCE_LABELS } from '../../constants/venueAccessibility';
import { filterTokens, focusRing, webTransition, usePrefersReducedMotion } from '../map-filter/mapFilterUi';

const isWeb = Platform.OS === 'web';
const TWO_COLUMN_MIN = 560;

const VALUE_TEXT = { [ACCESS_YES]: 'Yes', [ACCESS_NO]: 'No', [ACCESS_UNKNOWN]: 'Unknown' };

/**
 * REQ-104: "Accessibility" on the venue page and the map's venue details, split For fans / For artists.
 * Presentational: pass `resolved` from resolveVenueAccessibility(). Unknown rows fold into one
 * "N unknown" line so the section stays short.
 *
 * props: resolved, showGoogle (venue page only; Google rows never show on the Mapbox map),
 *        onAddNote, isDarkMode, colors: { text, muted, divider, surface } (optional, to sit on a
 *        host panel), fontFamily (optional)
 */
export default function VenueAccessibilitySection({
  resolved,
  showGoogle = false,
  onAddNote = null,
  isDarkMode = false,
  colors = null,
  fontFamily = null,
}) {
  const reduced = usePrefersReducedMotion();
  const [width, setWidth] = useState(0);
  if (!resolved) return null;
  const t = filterTokens(isDarkMode);
  const c = {
    text: colors?.text || t.pal.ink,
    muted: colors?.muted || (isDarkMode ? 'rgba(255,255,255,0.58)' : t.pal.mute),
    divider: colors?.divider || t.pal.line,
    surface: colors?.surface || (isDarkMode ? 'rgba(255,255,255,0.04)' : 'rgba(20,20,26,0.03)'),
  };
  const styles = makeStyles(t, c, isDarkMode, fontFamily);
  const twoCol = width >= TWO_COLUMN_MIN;
  const filterGoogle = (rows) => rows.map((row) => (
    !showGoogle && row.source === 'google' ? { ...row, value: ACCESS_UNKNOWN, source: null } : row
  ));

  return (
    <View
      style={styles.root}
      onLayout={(e) => setWidth(e.nativeEvent.layout.width)}
      accessibilityRole={isWeb ? undefined : 'summary'}
      {...(isWeb ? { role: 'region', 'aria-labelledby': 'venue-access-title' } : {})}
    >
      <View style={styles.head}>
        <Ionicons name="accessibility-outline" size={18} color={t.greenText} />
        <Text style={styles.title} nativeID="venue-access-title" accessibilityRole="header">Accessibility</Text>
      </View>

      <View style={[styles.columns, twoCol && styles.columnsRow]}>
        <AccessColumn
          title="For fans"
          rows={filterGoogle(resolved.fan)}
          notes={resolved.notes?.fan || []}
          styles={styles}
          t={t}
          isDarkMode={isDarkMode}
          reduced={reduced}
          onAddNote={onAddNote}
          twoCol={twoCol}
        />
        <AccessColumn
          title="For artists"
          rows={filterGoogle(resolved.artist)}
          notes={resolved.notes?.artist || []}
          freeText={resolved.artistText}
          styles={styles}
          t={t}
          isDarkMode={isDarkMode}
          reduced={reduced}
          onAddNote={onAddNote}
          twoCol={twoCol}
        />
      </View>

      {showGoogle && [...resolved.fan, ...resolved.artist].some((r) => r.source === 'google') ? (
        <Text style={styles.attribution}>Some details © Google</Text>
      ) : null}
      {[...resolved.fan, ...resolved.artist].some((r) => r.source === 'osm') ? (
        <Text style={styles.attribution}>Some details © OpenStreetMap contributors</Text>
      ) : null}
    </View>
  );
}

function StatusMark({ value, t, isDarkMode }) {
  if (value === ACCESS_YES) {
    return (
      <View style={[markBase, { backgroundColor: t.fill }]}>
        <Ionicons name="checkmark" size={12} color="#fffffe" />
      </View>
    );
  }
  if (value === ACCESS_NO) {
    const red = isDarkMode ? '#E08A80' : '#B23B30';
    return (
      <View style={[markBase, { borderWidth: 1.5, borderColor: red }]}>
        <Ionicons name="close" size={12} color={red} />
      </View>
    );
  }
  return (
    <View style={[markBase, { borderWidth: 1.5, borderStyle: 'dashed', borderColor: isDarkMode ? 'rgba(255,255,255,0.3)' : '#b9b9b3' }]} />
  );
}

const markBase = { width: 20, height: 20, borderRadius: 10, alignItems: 'center', justifyContent: 'center', flexShrink: 0 };

function AccessColumn({ title, rows, notes, freeText = '', styles, t, isDarkMode, reduced, onAddNote, twoCol }) {
  const [showUnknown, setShowUnknown] = useState(false);
  const known = rows.filter((r) => r.value !== ACCESS_UNKNOWN);
  const unknown = rows.filter((r) => r.value === ACCESS_UNKNOWN);
  const listId = `venue-access-${title.replace(/\s+/g, '-').toLowerCase()}-unknown`;

  const renderRow = (row) => (
    <View
      key={row.id}
      style={styles.row}
      accessible
      accessibilityLabel={`${row.label}: ${VALUE_TEXT[row.value]}${row.source ? `, ${ACCESS_SOURCE_LABELS[row.source]}` : ''}`}
    >
      <StatusMark value={row.value} t={t} isDarkMode={isDarkMode} />
      <View style={styles.rowTextWrap}>
        <Text style={[styles.rowLabel, row.value === ACCESS_UNKNOWN && styles.rowLabelUnknown]}>{row.label}</Text>
        <Text style={styles.rowMeta}>
          {VALUE_TEXT[row.value]}
          {row.source ? ` · ${ACCESS_SOURCE_LABELS[row.source]}` : ''}
        </Text>
      </View>
    </View>
  );

  return (
    <View style={[styles.column, twoCol && styles.columnHalf]}>
      <View style={styles.colHead}>
        <Text style={styles.colTitle} accessibilityRole="header">{title}</Text>
        <Text style={styles.colCount}>{known.length} of {rows.length} known</Text>
      </View>

      {known.map(renderRow)}

      {freeText ? (
        <View style={styles.noteBlock}>
          <Text style={styles.noteLabel}>Notes</Text>
          <Text style={styles.noteText}>{freeText}</Text>
        </View>
      ) : null}

      {notes.map((note, i) => (
        <View key={note.id || `n${i}`} style={styles.noteBlock}>
          <Text style={styles.noteLabel}>From a report{note.areaLabel ? ` · ${note.areaLabel}` : ''}</Text>
          <Text style={styles.noteText}>{note.text}</Text>
        </View>
      ))}

      {unknown.length > 0 ? (
        <View style={styles.unknownWrap}>
          <View style={styles.unknownBar}>
            <Pressable
              onPress={() => setShowUnknown((v) => !v)}
              accessibilityRole="button"
              accessibilityState={{ expanded: showUnknown }}
              accessibilityLabel={`${unknown.length} ${title.toLowerCase()} items unknown${showUnknown ? ', hide list' : ', show list'}`}
              {...(isWeb ? { 'aria-controls': listId, 'aria-expanded': showUnknown } : {})}
              style={({ hovered, focused }) => [styles.linkBtn, hovered && styles.linkBtnHover, webTransition(reduced), focusRing(focused, isDarkMode)]}
            >
              <Text style={styles.unknownText}>{unknown.length} {unknown.length === 1 ? 'item' : 'items'} unknown</Text>
              <Ionicons name={showUnknown ? 'chevron-up' : 'chevron-down'} size={13} color={styles.unknownText.color} />
            </Pressable>
            {onAddNote ? (
              <Pressable
                onPress={onAddNote}
                accessibilityRole="button"
                accessibilityLabel={`Add accessibility info ${title.toLowerCase()}`}
                style={({ hovered, focused }) => [styles.linkBtn, hovered && styles.linkBtnHover, webTransition(reduced), focusRing(focused, isDarkMode)]}
              >
                <Text style={styles.addText}>Add info</Text>
              </Pressable>
            ) : null}
          </View>
          {showUnknown ? <View nativeID={listId}>{unknown.map(renderRow)}</View> : null}
        </View>
      ) : null}
    </View>
  );
}

function makeStyles(t, c, isDarkMode, fontFamily) {
  const sans = fontFamily || t.sans;
  return StyleSheet.create({
    root: {
      width: '100%',
      paddingVertical: 16,
      paddingHorizontal: 4,
    },
    head: { flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 12, paddingHorizontal: 4 },
    title: { fontFamily: sans, fontSize: 18, fontWeight: '700', letterSpacing: -0.2, color: c.text },
    columns: { gap: 12 },
    columnsRow: { flexDirection: 'row', alignItems: 'flex-start' },
    column: {
      borderRadius: 10,
      borderWidth: 1,
      borderColor: c.divider,
      backgroundColor: c.surface,
      paddingVertical: 12,
      paddingHorizontal: 14,
      gap: 8,
    },
    columnHalf: { flex: 1, minWidth: 0 },
    colHead: { flexDirection: 'row', alignItems: 'baseline', justifyContent: 'space-between', gap: 8, marginBottom: 2 },
    colTitle: { fontFamily: t.mono, fontSize: 11.5, letterSpacing: 1.3, textTransform: 'uppercase', fontWeight: '600', color: c.text },
    colCount: { fontFamily: t.mono, fontSize: 10.5, letterSpacing: 0.6, color: c.muted },
    row: { flexDirection: 'row', alignItems: 'flex-start', gap: 10, paddingVertical: 2 },
    rowTextWrap: { flex: 1, minWidth: 0 },
    rowLabel: { fontFamily: sans, fontSize: 14, lineHeight: 19, color: c.text },
    rowLabelUnknown: { color: c.muted },
    rowMeta: { fontFamily: t.mono, fontSize: 10.5, letterSpacing: 0.4, color: c.muted, marginTop: 1 },
    noteBlock: {
      borderLeftWidth: 2,
      borderLeftColor: t.green,
      paddingLeft: 10,
      paddingVertical: 2,
    },
    noteLabel: { fontFamily: t.mono, fontSize: 10, letterSpacing: 1, textTransform: 'uppercase', color: c.muted, marginBottom: 2 },
    noteText: { fontFamily: sans, fontSize: 13.5, lineHeight: 19, color: c.text },
    unknownWrap: { gap: 6, marginTop: 2 },
    unknownBar: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 6 },
    linkBtn: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
      minHeight: 32,
      paddingHorizontal: 6,
      marginHorizontal: -6,
      borderRadius: 6,
      ...(isWeb ? { cursor: 'pointer' } : {}),
    },
    linkBtnHover: { backgroundColor: t.hover },
    unknownText: { fontFamily: sans, fontSize: 13, color: c.muted },
    addText: { fontFamily: sans, fontSize: 13, fontWeight: '600', color: t.greenText },
    attribution: { marginTop: 8, paddingHorizontal: 4, fontFamily: t.mono, fontSize: 10, color: c.muted },
  });
}
